// src/theme/theme.ts (or wherever your theme file is)

import { createTheme } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Theme {
    custom: {
      chart: {
        colors: string[];
        grid: string;
        axis: string;
        tooltipBg: string;
        tooltipText: string;
        gender: {
          male: string;
          female: string;
        };
      };
    };
  }
  interface ThemeOptions {
    custom?: {
      chart?: {
        colors?: string[];
        grid?: string;
        axis?: string;
        tooltipBg?: string;
        tooltipText?: string;
        gender?: {
          male?: string;
          female?: string;
        };
      };
    };
  }
}

const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#4880FF", // Your active connectivity blue
    },
    background: {
      default: "#1B2431", // Main page background
      paper: "#273142",   // Cards, tables, modals
    },
    text: {
      primary: "#FFFFFF",
      secondary: "#E5E7EB",
    },
    action: {
      hover: "#3A4659",
    },
  },

  typography: {
    fontFamily:
      'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 700 },
    h2: { fontWeight: 700 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    caption: { fontSize: "0.75rem", textTransform: "uppercase" },
  },

  components: {
    /* Smooth global transitions */
    MuiCssBaseline: {
      styleOverrides: {
        "*, *::before, *::after": {
          transition:
            "background-color 0.35s ease, color 0.35s ease, border-color 0.35s ease, fill 0.35s ease, stroke 0.35s ease, opacity 0.35s ease",
        },
        body: {
          backgroundColor: "#1B2431",
          color: "#E5E7EB",
        },
      },
    },

    /* TextField & Select */
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            backgroundColor: "#323D4E",
            color: "#E5E7EB",
            "& fieldset": {
              borderColor: "#4B5563",
            },
            "&:hover fieldset": {
              borderColor: "#5E6B7F",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#4880FF",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#91969E",
            "&.Mui-focused": { color: "#4880FF" },
          },
          "& .MuiInputBase-input::placeholder": {
            color: "#B0B7C3",
            opacity: 1,
          },
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          backgroundColor: "#323D4E",
          color: "#E5E7EB",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#4B5563",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#5E6B7F",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#4880FF",
          },
        },
        icon: {
          color: "#91969E",
        },
      },
    },

    /* Buttons */
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: "none",
          borderRadius: 8,
          transition:
            "background-color 0.35s ease, color 0.35s ease, transform 0.2s ease",
          "&:active": {
            transform: "scale(0.97)",
          },
        },
        contained: {
          backgroundColor: "#323D4E",
          color: "#FFFFFF",
          "&:hover": {
            backgroundColor: "#3A4659",
          },
        },
        text: {
          color: "#FF8743",
          "&:hover": {
            backgroundColor: "rgba(255, 135, 67, 0.1)",
          },
        },
      },
    },

    /* Cards / Paper */
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#273142",
          boxShadow: "0 10px 30px rgba(0, 0, 0, 0.4)",
        },
      },
    },

    /* Alerts */
    MuiAlert: {
      styleOverrides: {
        filledSuccess: { backgroundColor: "#22c55e" },
        filledError: { backgroundColor: "#ef4444" },
        filledWarning: { backgroundColor: "#f97316" },
      },
    },
  },

  /* Custom chart palette for dark mode */
  custom: {
    chart: {
      colors: ["#4880FF", "#10b981", "#f59e0b", "#ef4444", "#a78bfa", "#f43f5e"],
      grid: "#334155",
      axis: "#cbd5e1",
      tooltipBg: "#1e293b",
      tooltipText: "#f8fafc",
      gender: {
        male: "#4880FF",
        female: "#f43f5e",
      },
    },
  },
});

export default theme;